import arrayMutators from 'final-form-arrays';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import { Title } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { connectBabkaLoginOAuthAuth } from '../../../api';
import { GrantTypes } from '../../../api/configurations/oauth2/types';
import { DEFAULT_XSOLLA_ACCOUNT_VALUES } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { useProjects } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { FieldInput, FieldInputNumber, FieldTextarea, FormFooter, LinkFieldArray } from '../../ui';
import { addScope, deleteScope } from '../BabkaLoginClient/mutators';
import {
  ModalContentBox,
  SignatureMethodChangeWrapper,
  TokenExtra,
  Wrapper
} from '../BabkaLoginClient/style';
import {
  clientDescriptionMaxLength,
  clientNameMaxLength,
  FormValues,
  Oauth2ModalValidator
} from '../BabkaLoginClient/validator';
import { FormInfoCard, LogoFormField, ScopesFormField } from '../SettingsForm';

type Props = RouteComponentProps & {
  onClose: () => void;
};

const BabkaOauthModal = ({ history, onClose }: Props): JSX.Element => {
  const { babkaLoginPath } = usePaths();
  const intl = useIntl();

  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [baseNamesNumber, setBaseNamesNumber] = useState<number>(0);

  const { babkaLoginClient, setBabkaLoginClient } = useProjects();
  const { merchantId, projectId } = useConfig();
  const { showErrorNotify } = useNotifications();

  useEffect(() => {
    if (babkaLoginClient) {
      setBaseNamesNumber(
        babkaLoginClient.filter((client) =>
          client.name?.includes(
            intl.messages['authorization.settings.babkaLogin.client.name'] as string
          )
        ).length
      );
    }
  }, [babkaLoginClient]);

  const onSubmit = async (values: FormValues) => {
    const body = {
      access: [
        {
          resource: `${merchantId}`,
          resource_name: 'publisher_id'
        }
      ],
      audience: values.audience,
      description: values.description,
      grant_types: [
        GrantTypes.authorization_code,
        GrantTypes.client_credentials,
        GrantTypes.refresh_token
      ],
      icon: values.logoUrl,
      is_public: false,
      lifetime: Number(values.lifetime),
      name: values.name,
      partner_oauth2_page_url: DEFAULT_XSOLLA_ACCOUNT_VALUES.PARTNER_OAUTH2_PAGE_URL,
      publisher_id: merchantId,
      publisher_project_id: projectId,
      redirect_uris: values.redirect_uris,
      scopes: values.scopes
    };
    setFetching(true);
    connectBabkaLoginOAuthAuth(body)
      .then((res) => {
        setError(false);
        setBabkaLoginClient((prev) => [
          ...prev,
          {
            access: body.access,
            audience: body.audience,
            description: body.description,
            grant_types: body.grant_types,
            icon_url: body.icon,
            id: res.id,
            is_public: body.is_public,
            lifetime: body.lifetime,
            name: body.name,
            redirect_uris: body.redirect_uris,
            response_types: ['code'],
            scopes: body.scopes,
            secret: res.secret
          }
        ]);
        history.push({ pathname: babkaLoginPath(res.id) });
      })
      .catch((err) => {
        showErrorNotify(err);
        setError(true);
      })
      .finally(() => setFetching(false));
  };

  const setDefaultName = () => {
    return baseNamesNumber
      ? `${intl.formatMessage({
          id: 'authorization.settings.babkaLogin.client.name'
        })} (${baseNamesNumber + 1})`
      : intl.formatMessage({
          id: 'authorization.settings.babkaLogin.client.name'
        });
  };

  return (
    <Modal onClose={onClose} size="md">
      <Title indent="lg">
        {intl.messages['authorization.settings.babkaLogin.modal.connect.title']}
      </Title>
      <Form
        keepDirtyOnReinitialize
        onSubmit={onSubmit}
        initialValues={{
          name: setDefaultName(),
          redirect_uris: DEFAULT_XSOLLA_ACCOUNT_VALUES.REDIRECT_URIS,
          lifetime: 3600,
          scopes: DEFAULT_XSOLLA_ACCOUNT_VALUES.MANDATORY_SCOPES,
          audience: []
        }}
        validate={Oauth2ModalValidator}
        mutators={{ ...arrayMutators, addScope, deleteScope }}>
        {({ form, handleSubmit, pristine, errors, values: { scopes } }) => {
          return (
            <form onSubmit={handleSubmit}>
              <SignatureMethodChangeWrapper>
                <Wrapper>
                  <ModalContentBox>
                    <FormInfoCard />
                  </ModalContentBox>
                  <ModalContentBox>
                    <LogoFormField />
                  </ModalContentBox>
                  <ModalContentBox>
                    <FieldInput
                      label={intl.messages['authorization.settings.babkaLogin.field.name.label']}
                      name="name"
                      errorMessage={
                        errors?.name &&
                        intl.formatMessage(
                          {
                            id: errors?.name
                          },
                          {
                            number: clientNameMaxLength
                          }
                        )
                      }
                      placeholder={intl.formatMessage({
                        id: 'authorization.settings.oauth2.server.name.placeholder'
                      })}
                      data-test-id="oauth2-client-name"
                    />
                  </ModalContentBox>
                  <ModalContentBox>
                    <FieldTextarea
                      label={
                        intl.messages['authorization.settings.babkaLogin.field.description.label']
                      }
                      name="description"
                      errorMessage={
                        errors?.description &&
                        intl.formatMessage(
                          { id: errors?.description },
                          { number: clientDescriptionMaxLength }
                        )
                      }
                      resize
                      data-test-id="oauth2-client-description"
                    />
                  </ModalContentBox>
                  <ModalContentBox data-test-id="xsolla-account-redirect-urls-div">
                    <LinkFieldArray
                      name="redirect_uris"
                      label={intl.formatMessage({
                        id: 'authorization.settings.oauth2.modal.connect.redirect-uri.title'
                      })}
                      addLabel={intl.formatMessage({
                        id: 'configuration-page.urls.callback-urls.input.add-button'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                      })}
                      disabledValues={DEFAULT_XSOLLA_ACCOUNT_VALUES.REDIRECT_URIS}
                    />
                  </ModalContentBox>
                  <ModalContentBox data-test-id="xsolla-account-lifetime-div">
                    <FieldInputNumber
                      name="lifetime"
                      data-test-id="xsolla-account-lifetime"
                      hideArrows
                      subLabelCompact
                      label={intl.formatMessage({ id: 'settings.signature.token-lifetime.title' })}
                      errorMessage={
                        errors?.lifetime
                          ? intl.formatMessage(
                              { id: `${errors?.lifetime}` },
                              {
                                number:
                                  `${errors?.lifetime}` === 'app.validation.value-more'
                                    ? 432000
                                    : 300
                              }
                            )
                          : ''
                      }
                      subLabel={intl.formatMessage(
                        { id: 'settings.signature.token-lifetime.description-oauth' },
                        { br: () => <br /> }
                      )}
                      extra={() => (
                        <TokenExtra>
                          {intl.formatMessage({ id: 'settings.signature.token-lifetime.seconds' })}
                        </TokenExtra>
                      )}
                      extraAlign="right"
                    />
                  </ModalContentBox>
                  <ModalContentBox data-test-id="xsolla-account-scope-div">
                    <ScopesFormField
                      dataTestId="xsolla-account-page-scope"
                      scopes={scopes}
                      disabledScopes={DEFAULT_XSOLLA_ACCOUNT_VALUES.MANDATORY_SCOPES}
                      hiddenScopes={DEFAULT_XSOLLA_ACCOUNT_VALUES.HIDDEN_SCOPES}
                      onAdd={form.mutators.addScope}
                      onDelete={form.mutators.deleteScope}
                    />
                  </ModalContentBox>
                  <ModalContentBox data-test-id="xsolla-account-audience-div">
                    <LinkFieldArray
                      name="audience"
                      label={intl.formatMessage({
                        id: 'authorization.settings.babkaLogin.modal.connect.audience.title'
                      })}
                      addLabel={intl.formatMessage({
                        id: 'configuration-page.urls.callback-urls.input.add-button'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                      })}
                    />
                  </ModalContentBox>
                </Wrapper>
              </SignatureMethodChangeWrapper>

              <FormFooter
                onSubmit={() => onSubmit}
                onCancel={onClose}
                fetching={fetching}
                error={error}
                disabled={pristine}
              />
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default withRouter(BabkaOauthModal);
